import React, { useCallback, useState } from 'react';

import OvertimeConfirmationModal from '@/routes/Agency/CreateGigOrder/JobEditor/Steps/StaffingStep/OvertimeConfirmationModal';
import { GAEvent } from '@/constants/gaevents';
import { JobsAPI, OvertimeProjection } from '@/services/jobsAPI';
import useAnalytics from '@/util/analytics';
import {
  trackOvertimeModalConfirmed,
  trackOvertimeModalDismissed,
  trackOvertimeModalShown,
} from '@/util/vexo';
import { workerName } from '@/util/worker';

type GateMode = 'hire' | 'invite' | 'remove';

type CheckOptions = {
  // Skip the gate entirely (used on rehire / already-offered overrides where
  // the admin has already accepted a prior confirmation modal).
  force?: boolean;
  // If the caller already has the projection (e.g. valid_workers decorates it
  // on the worker object), pass it here to skip the REST roundtrip. Pass
  // `null` to mean "I checked and there's no applicable projection".
  prefetchedProjection?: OvertimeProjection | null;
  // For surfaces that don't have a Job id yet (the order-creation wizard sees
  // schedules in memory before submitting). The hook calls the
  // overtime_projection_from_shifts endpoint instead of the per-Job one.
  // Ignored when `prefetchedProjection` is also provided.
  //
  // GRAVY-10073 break-aware: each shift may carry `mandatory_break_minutes`
  // so the projection subtracts break time when computing per-week
  // overtime. Missing field defaults to 0 server-side.
  proposedShifts?: Array<{
    start_at: string;
    end_at: string;
    mandatory_break_minutes?: number;
  }>;
};

// Surfaces use this to instrument `worker_invited` / `worker_hired` with
// the same OT context the modal saw. `triggered: false` means the modal
// did not show (either the gate short-circuited via remove/force, the
// projection was nil, or projected hours were under 40).
export type OvertimeOutcome = {
  triggered: boolean;
  worker_current_hours: number | null;
  overtime_hours: number | null;
  already_in_overtime: boolean;
};

const NULL_OUTCOME: OvertimeOutcome = {
  triggered: false,
  worker_current_hours: null,
  overtime_hours: null,
  already_in_overtime: false,
};

// Sum hours across the impacted weeks of a projection. The projection
// itself reports per-week numbers; the Vexo events want totals so a single
// shift event captures the cumulative picture.
const summarizeProjection = (
  projection: OvertimeProjection,
): {
  worker_current_hours: number;
  shift_hours: number;
  projected_total_hours: number;
  overtime_hours: number;
  already_in_overtime: boolean;
} => {
  const weeks = projection.weeks ?? [];
  const worker_current_hours = weeks.reduce(
    (sum, w) => sum + (w.current_hours ?? 0),
    0,
  );
  const projected_total_hours = weeks.reduce(
    (sum, w) => sum + (w.projected_hours ?? 0),
    0,
  );
  const overtime_hours = weeks.reduce((sum, w) => sum + (w.ot_hours ?? 0), 0);
  const already_in_overtime = weeks.some((w) => (w.current_hours ?? 0) > 40);
  return {
    worker_current_hours: Number(worker_current_hours.toFixed(2)),
    shift_hours: Number(
      (projected_total_hours - worker_current_hours).toFixed(2),
    ),
    projected_total_hours: Number(projected_total_hours.toFixed(2)),
    overtime_hours: Number(overtime_hours.toFixed(2)),
    already_in_overtime,
  };
};

// Accepts any object that has at least an `id`. We type as Record<string, any>
// so the hook composes with the various worker shapes used across the app
// (REST autocomplete, GraphQL fragments, decorated valid_workers rows, ...).
type WorkerLike = Record<string, any>;

type ProjectionSummary = ReturnType<typeof summarizeProjection>;

type Pending = {
  jobId: string | number | null;
  worker: WorkerLike;
  projection: OvertimeProjection;
  // Synthesized at gate-fire time so the proceed/cancel callbacks
  // reflect any total-proposed-hours override applied to the raw
  // per-week summary (used when the cross-week 45h-job rule fires).
  summary: ProjectionSummary;
  mode: GateMode;
  onProceed: (outcome: OvertimeOutcome) => void;
  onCancel?: () => void;
};

/**
 * useOvertimeGate centralises the W2 over-40 confirmation flow shared by every
 * invite/hire entry point in tenant-admin. Callers wrap their submit in
 * `check(jobId, worker, mode, onProceed)`. If the projection trips the 40h
 * threshold for a W2 worker, the hook renders OvertimeConfirmationModal and
 * runs `onProceed` only after the admin confirms. Otherwise `onProceed` runs
 * synchronously.
 *
 * `onProceed` receives an `OvertimeOutcome` so callers can fire `worker_invited`
 * / `worker_hired` Vexo events with the same OT context the modal saw.
 *
 * Render `{modal}` once near the consumer's existing modal/portal output.
 *
 * Concurrency: the hook owns a single `pending` slot, so two parallel `check`
 * calls would overwrite each other. Today's callers either serialize via a
 * per-job `await new Promise(...)` loop (InviteAndHireModal, WorkerStaffingModel)
 * or gate behind an `isLoading` submit button (NewOfferModal, AddWorkerModal),
 * so this is safe. If a future caller fans out parallel checks, swap the slot
 * for a queue.
 */
export const useOvertimeGate = () => {
  const { logEvent } = useAnalytics();
  const [pending, setPending] = useState<Pending | null>(null);

  const check = useCallback(
    async (
      jobId: string | number | null,
      worker: WorkerLike,
      mode: GateMode,
      onProceed: (outcome: OvertimeOutcome) => void,
      opts: CheckOptions & { onCancel?: () => void } = {},
    ) => {
      if (mode === 'remove' || opts.force) {
        onProceed(NULL_OUTCOME);
        return;
      }
      let projection: OvertimeProjection | null;
      if (opts.prefetchedProjection !== undefined) {
        projection = opts.prefetchedProjection;
      } else if (jobId != null) {
        projection = await JobsAPI.overtimeProjection(jobId, worker.id);
      } else if (opts.proposedShifts && opts.proposedShifts.length > 0) {
        projection = await JobsAPI.overtimeProjectionFromShifts(
          worker.id,
          opts.proposedShifts,
        );
      } else {
        projection = null;
      }
      // The backend reports `exceeds_40` only when *some single week's*
      // `current + job` total crosses the 40h threshold. That misses a
      // common admin warning case: a job whose shifts straddle a Mon–Sun
      // boundary so neither week alone exceeds 40, but the worker is
      // still being scheduled for >40h on this job. Layer a total-
      // proposed-hours check on top of the projection so multi-week
      // 45h+ jobs surface the modal.
      //
      // GRAVY-10073: deduct each shift's `mandatory_break_minutes` so this
      // fallback measures *net* worked hours, matching the break-aware
      // backend projection. Summing gross duration here produced false
      // positives (e.g. 5×8.5h gross with 30m breaks = 42.5h gross but
      // 40.0h net — under the threshold).
      const totalProposedHours = (opts.proposedShifts ?? []).reduce(
        (sum, s) => {
          const startMs = new Date(s.start_at).getTime();
          const endMs = new Date(s.end_at).getTime();
          if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) return sum;
          const grossHours = (endMs - startMs) / 3_600_000;
          if (grossHours <= 0) return sum;
          const netHours = grossHours - (s.mandatory_break_minutes ?? 0) / 60;
          return netHours > 0 ? sum + netHours : sum;
        },
        0,
      );
      // `projection != null` already implies the worker is W2 — the
      // jobsAPI client normalizes `applicable: false` to null, so 1099
      // workers can't trip this branch even when their proposed shifts
      // total >40 (no OT obligation on 1099).
      const totalExceeds40 = !!projection && totalProposedHours > 40;
      const shouldFireModal =
        !!projection && (projection.exceeds_40 || totalExceeds40);

      if (shouldFireModal) {
        const baseSummary = summarizeProjection(projection!);
        // When the modal fires *only* because of the total-hours branch
        // (no individual week crossed 40), the per-week ot_hours sum is
        // 0 — synthesize a sensible total-OT delta so Vexo events and
        // the OvertimeOutcome carry the real magnitude.
        const summary = projection!.exceeds_40
          ? baseSummary
          : {
              ...baseSummary,
              shift_hours: Number(totalProposedHours.toFixed(2)),
              projected_total_hours: Number(
                (baseSummary.worker_current_hours + totalProposedHours).toFixed(
                  2,
                ),
              ),
              overtime_hours: Number(
                Math.max(totalProposedHours - 40, 0).toFixed(2),
              ),
            };
        logEvent(GAEvent.OvertimeWarningShown, worker.id, {
          workerId: worker.id,
        });
        // Vexo `overtime_modal_shown` fires alongside the GA event so the
        // worker-actions funnel sees the same shape on both sinks.
        trackOvertimeModalShown({
          job_id: jobId,
          worker_id: worker.id,
          worker_current_hours: summary.worker_current_hours,
          shift_hours: summary.shift_hours,
          projected_total_hours: summary.projected_total_hours,
          overtime_hours: summary.overtime_hours,
          action_type: mode === 'hire' ? 'hire' : 'invite',
          already_in_overtime: summary.already_in_overtime,
        });
        setPending({
          jobId,
          worker,
          projection: projection!,
          summary,
          mode,
          onProceed,
          onCancel: opts.onCancel,
        });
        return;
      }
      onProceed(NULL_OUTCOME);
    },
    [logEvent],
  );

  const modal = pending ? (
    <OvertimeConfirmationModal
      isOpen
      workerName={workerName(pending.worker) || 'this worker'}
      projection={pending.projection}
      onProceed={() => {
        const summary = pending.summary;
        logEvent(GAEvent.OvertimeWarningConfirmed, pending.worker.id, {
          workerId: pending.worker.id,
        });
        trackOvertimeModalConfirmed({
          job_id: pending.jobId,
          worker_id: pending.worker.id,
          overtime_hours: summary.overtime_hours,
          action_type: pending.mode === 'hire' ? 'hire' : 'invite',
          already_in_overtime: summary.already_in_overtime,
        });
        const fn = pending.onProceed;
        setPending(null);
        fn({
          triggered: true,
          worker_current_hours: summary.worker_current_hours,
          overtime_hours: summary.overtime_hours,
          already_in_overtime: summary.already_in_overtime,
        });
      }}
      onCancel={() => {
        const summary = pending.summary;
        logEvent(GAEvent.OvertimeWarningCancelled, pending.worker.id, {
          workerId: pending.worker.id,
        });
        // OvertimeConfirmationModal only exposes a single onCancel today;
        // future product spec may split "Go back" footer button from the
        // "×" close button. Default to 'go_back' for now.
        trackOvertimeModalDismissed({
          job_id: pending.jobId,
          worker_id: pending.worker.id,
          overtime_hours: summary.overtime_hours,
          dismiss_method: 'go_back',
          already_in_overtime: summary.already_in_overtime,
        });
        const fn = pending.onCancel;
        setPending(null);
        if (fn) fn();
      }}
    />
  ) : null;

  return { check, modal };
};

export default useOvertimeGate;
